import { useMemo, useState } from 'react';
import { useOpsPage } from '../hooks/useDailyInstallations';
import OpsRecordList, { ukDate } from '../components/OpsRecordList';
import {
  BRAND, BarRow, DayChart, Note, PageHeader, Panel, States, Stat,
  num, pct, selectClass, thisMonth,
} from '../components/OpsPageKit';

// Internal Remediations — the NEO2 rebuild.
//
// What is being sent back for rework, and why. The six reasons below are the
// ones the report puts on cards of their own; the full list sits underneath so
// a reason that grows is visible before it earns a card.
function InternalRemediations() {
  const [month, setMonth] = useState(thisMonth());
  const [region, setRegion] = useState('');

  const filters = useMemo(() => ({ month, region }), [month, region]);
  const {
    data, isLoading, isFetching, refetch, isError,
  } = useOpsPage('internal-remediations', filters);

  const t = data?.totals || {};
  const regions = (data?.by_region || []).map((r) => r.region);
  const maxSource = Math.max(1, ...(data?.sources || []).map((r) => r.n));
  const maxEngineer = Math.max(1, ...(data?.by_engineer || []).map((r) => r.n));

  return (
    <div className="p-6">
      <PageHeader
        title="Internal Remediations"
        subtitle="What is going back for rework, why, and who raised it."
        month={month}
        onMonth={setMonth}
        onRefresh={refetch}
        fetching={isFetching}
      >
        <select value={region} onChange={(e) => setRegion(e.target.value)} className={selectClass}>
          <option value="">All areas</option>
          {regions.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </PageHeader>

      <States isLoading={isLoading} isError={isError} what="internal remediations" />

      {data && (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="Submissions" value={num(t.submissions)} sub={`${t.engineers} engineers`} accent />
            <Stat label="Existing low dropwire" value={num(t.existing_low_dropwire)} />
            <Stat label="New dropwire below 5.9m" value={num(t.new_dropwire_below_59)} />
            <Stat label="No light" value={num(t.no_light)} />
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="Saturn 104" value={num(t.saturn_104)} />
            <Stat label="POC replacement" value={num(t.poc_replacement)} />
            <Stat
              label="Asbestos register"
              value={num(t.asbestos_register)}
              tone={t.asbestos_register ? '#DC2626' : undefined}
            />
            <Stat label="Named submissions" value={num(t.by_engineer)} sub="excluding Nano Bot" />
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Panel title="Raised by day">
              <DayChart rows={data.by_day} series={[{ key: 'n', label: 'Remediations', colour: BRAND }]} />
            </Panel>

            <Panel title="Every reason">
              <div className="py-1">
                {data.sources.map((r) => (
                  <BarRow
                    key={r.source}
                    label={r.source}
                    value={r.n}
                    total={maxSource}
                    sub={pct(t.submissions ? r.n / t.submissions : 0)}
                  />
                ))}
              </div>
            </Panel>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Panel title="Most remediations raised">
              <div className="py-1">
                {data.by_engineer.map((r) => (
                  <BarRow key={r.engineer} label={r.engineer} value={r.n} total={maxEngineer} colour="#D97706" />
                ))}
              </div>
            </Panel>

            <Panel title="By region">
              <div className="py-1">
                {data.by_region.map((r) => (
                  <BarRow
                    key={r.region}
                    label={r.region}
                    value={r.n}
                    total={Math.max(1, ...data.by_region.map((x) => x.n))}
                    colour="#0891B2"
                  />
                ))}
              </div>
            </Panel>
          </div>

          <Note>
            The BRSK list records an engineer name on some rows and an email on most; the Netomnia
            list records neither, only who handled it. The engineer shown is the name where there
            is one, otherwise the directory name for the email, otherwise the handler — without
            that fallback a whole contract would read as zero. Submissions by the automation
            account are excluded from the engineer count, as in the report.
          </Note>
        </>
      )}

      <OpsRecordList
        page="internal-remediations"
        title="Remediations"
        searchPlaceholder="Address, CBT, work order, reason, handler, name…"
        columns={[
          { head: 'Date', className: 'whitespace-nowrap tabular-nums text-gray-800', cell: (r) => ukDate(r.remediation_date) },
          { head: 'Engineer', className: 'text-gray-800', cell: (r) => r.engineer || '—' },
          { head: 'Region', cell: (r) => ({ BRSK: 'Region B', Netomnia: 'Region N' }[r.contract] || r.contract || '—') },
          { head: 'Area', cell: (r) => r.region || '—' },
          { head: 'Address', className: 'max-w-[16rem] text-gray-600', cell: (r) => (<>{r.address || '—'}{(r.cbt_number || r.work_order) ? <div className="text-xs text-gray-400">{[r.work_order, r.cbt_number && `CBT ${r.cbt_number}`].filter(Boolean).join(' · ')}</div> : null}</>) },
          { head: 'Reason', className: 'max-w-[14rem] text-gray-700', cell: (r) => r.remediation_reason || '—' },
          { head: 'Outcome', cell: (r) => (<>{r.approval_outcome || '—'}{r.handler ? <div className="text-xs text-gray-400">{r.handler}</div> : null}</>) },
        ]}
      />
    </div>
  );
}

export default InternalRemediations;
